import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import SceneStage from "./SceneStage";
import Navigation from "./Navigation";
import PresenterMode from "./PresenterMode";
import useKeyboard from "./hooks/useKeyboard";
import { scenes } from "./scenes";
import { postSync, subscribeSync } from "./sync";

/**
 * PresentationApp — top-level orchestrator for the keynote.
 * Owns: index, direction, presenter mode, fullscreen.
 * Also bridges state across windows via BroadcastChannel so Presenter View stays in sync.
 */
export default function PresentationApp() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [presenterOpen, setPresenterOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [introComplete, setIntroComplete] = useState(false);
  const presenterWinRef = useRef(null);
  /**
   * Optional per-scene guard that intercepts the next-advance event.
   * If set and returns `true`, the slide does not advance — the scene has
   * handled the advance internally (progressive disclosure within a slide).
   */
  const advanceGuardRef = useRef(null);

  const total = scenes.length;

  const goTo = useCallback(
    (next) => {
      setDirection(next > index ? 1 : -1);
      setIndex((prev) => Math.max(0, Math.min(total - 1, next)));
      setPresenterOpen(false);
    },
    [index, total],
  );

  const handleNext = useCallback(() => {
    if (presenterOpen) {
      setPresenterOpen(false);
      return;
    }
    // Let the active scene intercept the advance (for progressive disclosure).
    if (advanceGuardRef.current && advanceGuardRef.current() === true) {
      return;
    }
    if (index < total - 1) {
      setDirection(1);
      setIndex(index + 1);
    }
  }, [index, total, presenterOpen]);

  const handlePrev = useCallback(() => {
    if (presenterOpen) {
      setPresenterOpen(false);
      return;
    }
    if (index > 0) {
      setDirection(-1);
      setIndex(index - 1);
    }
  }, [index, presenterOpen]);

  const handleFirst = useCallback(() => {
    setDirection(-1);
    setIndex(0);
  }, []);

  const handleLast = useCallback(() => {
    setDirection(1);
    setIndex(total - 1);
  }, [total]);

  const togglePresenter = useCallback(() => {
    setPresenterOpen((v) => !v);
  }, []);

  const openPresenterWindow = useCallback(() => {
    // Open Presenter View on a separate browser window so you can drag it
    // to your second screen. Re-focus if already open.
    if (presenterWinRef.current && !presenterWinRef.current.closed) {
      presenterWinRef.current.focus();
      return;
    }
    const features = [
      "popup=yes",
      "width=1280",
      "height=820",
      "left=80",
      "top=80",
      "noopener=no",
    ].join(",");
    presenterWinRef.current = window.open("/presenter", "keynote-presenter", features);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch {
      // Permissions might be denied in iframes — non-fatal.
    }
  }, []);

  const handleEscape = useCallback(() => {
    if (presenterOpen) setPresenterOpen(false);
  }, [presenterOpen]);

  useKeyboard({
    onNext: handleNext,
    onPrev: handlePrev,
    onFirst: handleFirst,
    onLast: handleLast,
    onTogglePresenter: togglePresenter,
    onToggleFullscreen: toggleFullscreen,
    onEscape: handleEscape,
    onOpenPresenterView: openPresenterWindow,
  });

  // Cross-window sync: react to messages and broadcast our state on changes.
  useEffect(() => {
    const unsub = subscribeSync((msg) => {
      if (!msg) return;
      if (msg.type === "GO" && typeof msg.index === "number") {
        setDirection(msg.index > index ? 1 : -1);
        setIndex(Math.max(0, Math.min(total - 1, msg.index)));
        setPresenterOpen(false);
      } else if (msg.type === "STEP") {
        if (msg.dir === 1) handleNext();
        else if (msg.dir === -1) handlePrev();
      } else if (msg.type === "TOGGLE_PRESENTER") {
        togglePresenter();
      } else if (msg.type === "REQUEST_STATE") {
        postSync({ type: "STATE", index, presenterOpen, ts: Date.now() });
      }
    });
    return unsub;
  }, [index, total, handleNext, handlePrev, togglePresenter, presenterOpen]);

  // Broadcast our state every time it changes (so Presenter View follows).
  useEffect(() => {
    postSync({ type: "STATE", index, presenterOpen, ts: Date.now() });
  }, [index, presenterOpen]);

  // Track fullscreen state
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  // Brief curtain on first load for a "lights up" feel
  useEffect(() => {
    const t = setTimeout(() => setIntroComplete(true), 700);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className="relative h-screen w-screen overflow-hidden bg-ink"
      data-testid="presentation-root"
    >
      {/* The active scene */}
      <SceneStage
        index={index}
        direction={direction}
        scene={scenes[index]}
        onAdvance={handleNext}
        advanceGuardRef={advanceGuardRef}
      />

      {/* Overlay UI */}
      <Navigation
        index={index}
        total={total}
        scenes={scenes}
        onPrev={handlePrev}
        onNext={handleNext}
        onJump={goTo}
        onTogglePresenter={togglePresenter}
        onToggleFullscreen={toggleFullscreen}
        onOpenPresenterView={openPresenterWindow}
        isFullscreen={isFullscreen}
      />

      {/* Presenter overview overlay */}
      <PresenterMode
        open={presenterOpen}
        scenes={scenes}
        index={index}
        onJump={goTo}
        onClose={() => setPresenterOpen(false)}
      />

      {/* First-load curtain */}
      <AnimatePresence>
        {!introComplete ? (
          <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0 z-[60] bg-black"
            data-testid="intro-curtain"
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}
